import { useCallback, useEffect, useRef, type RefObject } from 'react';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
} from 'echarts/components';
import { LegacyGridContainLabel } from 'echarts/features';
import { useUiStore } from '@/store/ui-store';
import { CHART_PALETTES } from '@/themes/theme-config';

// Register only the parts we use — tree-shakes the rest.
// LegacyGridContainLabel keeps `grid.containLabel: true` working under
// ECharts 6 without the deprecation warning. ECharts logs that warning
// on every setOption call when the feature isn't registered, which
// floods the DevTools console and eventually freezes the page when the
// console buffer grows under repeated chart re-renders.
// GraphicComponent powers the in-canvas "Toplam: N" badge on pie charts;
// without it ECharts silently drops the `graphic` option and the badge
// never appears (live nor in PNG export).
echarts.use([
  CanvasRenderer,
  BarChart,
  LineChart,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
  LegacyGridContainLabel,
]);

export type EChartsClickParams = {
  name: string;
  seriesName: string;
  dataIndex: number;
  value: unknown;
};

type UseEChartsReturn = {
  /** Attach this ref to the chart container div. */
  chartRef: RefObject<HTMLDivElement>;
  /** Retrieve the underlying ECharts instance safely. */
  getInstance: () => echarts.ECharts | null;
};

/**
 * Manages a single ECharts instance lifecycle.
 *
 * - Creates on mount, disposes on unmount (strict-mode safe).
 * - Calls `setOption` whenever `option` changes.
 * - Auto-resizes via ResizeObserver.
 * - Optional `onClick` handler for drill-down.
 * - Reacts to theme changes and updates chart color palette.
 */
export function useEChartsInstance(
  option: echarts.EChartsCoreOption | null,
  onClick?: (params: EChartsClickParams) => void,
): UseEChartsReturn {
  const chartRef = useRef<HTMLDivElement>(null!);
  const instanceRef = useRef<echarts.ECharts | null>(null);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  const theme = useUiStore((s) => s.theme);

  // Init / dispose lifecycle
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;

    const instance = echarts.init(el);
    instanceRef.current = instance;

    // Click handler for drill-down
    instance.on('click', (params: unknown) => {
      const p = params as EChartsClickParams;
      onClickRef.current?.(p);
    });

    // Resize observer for responsive charts — rAF-throttled so that
    // continuous resize events during react-grid-layout drags coalesce
    // into at most one resize per frame.
    let rafId = 0;
    const ro = new ResizeObserver(() => {
      if (rafId !== 0) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        instance.resize();
      });
    });
    ro.observe(el);

    return () => {
      if (rafId !== 0) cancelAnimationFrame(rafId);
      ro.disconnect();
      instance.dispose();
      instanceRef.current = null;
    };
  }, []);

  // Update option — honour a widget-supplied `color` palette; fall back to
  // the theme palette only when the widget hasn't picked one.
  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !option) return;
    const widgetColor = (option as { color?: readonly string[] }).color;
    const color =
      widgetColor && widgetColor.length > 0
        ? [...widgetColor]
        : [...CHART_PALETTES[theme]];
    instance.setOption({ ...option, color }, { notMerge: true });
  }, [option, theme]);

  // Stable closure so consumers (chart registry, export buttons) don't
  // see a fresh `getInstance` reference on every render and re-fire
  // their effects (register → unregister → register on each paint).
  const getInstance = useCallback(() => instanceRef.current, []);
  return { chartRef, getInstance };
}
